import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import type { TeammatePlaybookToolUsageAccuracyScore } from "@repo/app-types/entities/teammatePerformanceSummary";
import { SlideoverContainer } from "@repo/ui/Slideover/slideoverContainer";
import type { FC } from "react";
import { useMemo, useState } from "react";
import { useAllToolsById } from "../../hooks/useAllToolsById.js";
import { ToolUsageAccuracyOverviewCard } from "./toolUsageAccuracyOverviewCard.js";
import { ToolUsageAccuracySlideoverDialog } from "./toolUsageAccuracySlideoverDialog.js";

export interface ToolUsageAccuracyOverviewCardContainerProps {
  playbookId: PlaybookId | undefined;
  teammateId: TeammateId | undefined;
  toolIds: readonly string[];
  data: readonly TeammatePlaybookToolUsageAccuracyScore[] | undefined;
  isLoading?: boolean;
}

/**
 * Container component that fetches tools and builds the score map,
 * then renders ToolUsageAccuracyOverviewCard.
 */
export const ToolUsageAccuracyOverviewCardContainer: FC<
  ToolUsageAccuracyOverviewCardContainerProps
> = (props) => {
  const {
    playbookId,
    teammateId,
    data: toolUsageAccuracyScores,
    isLoading,
  } = props;

  const allTools = useAllToolsById();

  const toolScores = useMemo(() => {
    const map = new Map<string, TeammatePlaybookToolUsageAccuracyScore>();
    for (const score of toolUsageAccuracyScores ?? []) {
      map.set(score.tool_id, score);
    }
    return map;
  }, [toolUsageAccuracyScores]);

  const [openedToolId, setOpenedToolId] = useState<string | undefined>(
    undefined,
  );

  const handleToolClick = (toolId: string): void => {
    setOpenedToolId(toolId);
  };

  const handleSlideoverOpenChange = (newIsOpen: boolean): void => {
    if (!newIsOpen) {
      setOpenedToolId(undefined);
    }
  };

  return (
    <>
      <ToolUsageAccuracyOverviewCard
        toolIds={props.toolIds}
        tools={allTools}
        toolScores={toolScores}
        isLoading={isLoading}
        onToolClick={handleToolClick}
      />

      <SlideoverContainer
        isDismissable
        isOpen={openedToolId !== undefined}
        onOpenChange={handleSlideoverOpenChange}
      >
        {openedToolId && playbookId && teammateId ? (
          <ToolUsageAccuracySlideoverDialog
            playbookId={playbookId}
            teammateId={teammateId}
            toolId={openedToolId}
            toolName={allTools.get(openedToolId)?.name ?? "Unknown tool"}
          />
        ) : null}
      </SlideoverContainer>
    </>
  );
};
